// Tradução em pt-br dos motivos de falha de envio.
//
// O campo `erro.text` do documento guarda o motivo CRU: às vezes vem do Baileys
// em inglês ("Connection Closed"), às vezes do axios ao baixar um anexo
// ("Request failed with status code 404"), às vezes da Cloud API da Meta com
// código estruturado. Jogar isso direto na tela do cliente final não ajuda
// ninguém — ele não sabe o que fazer e abre chamado.
//
// Cada entrada devolve título curto, explicação e a ação sugerida. O texto
// original NUNCA é descartado: fica em `tecnico`, atrás de "ver detalhe
// técnico", que é o que o suporte precisa para agir rápido.

// Erros da Cloud API da Meta, casados pelo código que ela devolve.
const META_POR_CODIGO = {
  131047: {
    titulo: "Cliente não respondeu nas últimas 24h",
    explicacao:
      "O WhatsApp só permite iniciar conversa fora da janela de 24h usando um modelo aprovado. Como o cliente não respondeu recentemente, a mensagem livre não é entregue.",
    acao: "Use um modelo aprovado para reabrir a conversa.",
  },
  131026: {
    titulo: "Mensagem não pôde ser entregue",
    explicacao:
      "O número pode não ter WhatsApp, estar incorreto, ou o aparelho não conseguiu receber. Também ocorre quando o destinatário não aceita mensagens da sua conta.",
    acao: "Confira se o número está correto e tem WhatsApp ativo.",
  },
  131049: {
    titulo: "Entrega bloqueada pela Meta (saúde da conta)",
    explicacao:
      "A Meta limitou esta entrega para preservar a experiência do usuário — geralmente por volume alto de marketing ou queda na qualidade do número.",
    acao: "Reduza envios em massa e priorize mensagens relevantes.",
  },
  131042: {
    titulo: "Pendência de pagamento na conta do WhatsApp",
    explicacao:
      "A Meta recusou o envio porque a conta do WhatsApp Business está sem método de pagamento válido ou com cobrança pendente.",
    acao: "Regularize o método de pagamento no Gerenciador da Meta.",
  },
  131031: {
    titulo: "Conta restrita ou bloqueada",
    explicacao:
      "A conta do WhatsApp Business foi restrita pela Meta e não pode enviar mensagens no momento.",
    acao: "Verifique o status da conta no Gerenciador da Meta e regularize as pendências.",
  },
  368: {
    titulo: "Conta temporariamente bloqueada (política)",
    explicacao: "A conta foi bloqueada temporariamente por violação das políticas do WhatsApp.",
    acao: "Revise as políticas e aguarde a liberação; reincidências aumentam o bloqueio.",
  },
  130429: {
    titulo: "Limite de envio atingido",
    explicacao:
      "Você atingiu o limite de velocidade ou quantidade de mensagens permitido pela Meta neste período.",
    acao: "Aguarde e reduza o ritmo de envio. O limite aumenta conforme a conta amadurece.",
  },
  132000: {
    titulo: "Quantidade de variáveis não bate com o modelo",
    explicacao: "O número de variáveis enviadas é diferente do que o modelo aprovado espera.",
    acao:
      "Ajuste o modelo no seu sistema para enviar exatamente as variáveis que o modelo aprovado usa.",
  },
  132001: {
    titulo: "Modelo não encontrado",
    explicacao: "O modelo informado não existe, não está aprovado, ou o idioma enviado não confere.",
    acao: "Confira o nome e o idioma do modelo no Gerenciador da Meta.",
  },
  132005: {
    titulo: "Conteúdo difere do modelo aprovado",
    explicacao: "O texto enviado não corresponde ao conteúdo que a Meta aprovou para este modelo.",
    acao: "Reenvie usando exatamente o conteúdo aprovado do modelo.",
  },
  132007: {
    titulo: "Conteúdo viola as políticas",
    explicacao: "O conteúdo do modelo foi recusado por violar as políticas do WhatsApp.",
    acao: "Revise o texto do modelo e submeta uma nova versão para aprovação.",
  },
  132012: {
    titulo: "Formato de variável inválido",
    explicacao: "Algum parâmetro enviado está em formato inválido para o modelo.",
    acao: "Verifique os valores das variáveis enviadas pelo seu sistema.",
  },
  133010: {
    titulo: "Número não registrado",
    explicacao: "O número da sua conta não está registrado ou ativo na API Oficial.",
    acao: "Finalize o registro do número no Gerenciador da Meta.",
  },
  100: {
    titulo: "Parâmetro inválido na requisição",
    explicacao: "Algum dado enviado à Meta está inválido ou faltando.",
    acao: "Revise o modelo e as variáveis; veja o detalhe técnico para o campo exato.",
  },
};

// Motivos casados pelo texto do erro. A ordem importa: o primeiro padrão que
// casar vence, então os mais específicos vêm antes dos genéricos.
const POR_TEXTO = [
  {
    // O caso mais comum do dia a dia: a conexão do número caiu no meio do
    // disparo. Quando a mensagem chega assim mesmo, o relatório já mostra como
    // entregue e este motivo nem aparece — ver `entregueApesarDoErro`.
    re: /connection closed|connection terminated|connection lost|socket.*clos|lost connection/i,
    titulo: "A conexão do número caiu durante o envio",
    explicacao:
      "O WhatsApp encerrou a conexão enquanto a mensagem saía. Não significa que o número foi desconectado de vez — costuma ser queda de rede ou reconexão automática.",
    acao:
      "Confira em Gerenciar API se o número segue conectado. Se estiver, é só reenviar; se caiu, leia o QR Code novamente.",
  },
  {
    re: /instância desconectada no momento do envio|bot da instância é inválido/i,
    titulo: "Número desconectado no momento do envio",
    explicacao:
      "A conexão não estava ativa quando a mensagem foi processada. Ela continua na fila e sai sozinha assim que o número reconectar.",
    acao: "Não é preciso reenviar. Se o número seguir desconectado, leia o QR Code novamente.",
  },
  {
    re: /timed? ?out|etimedout/i,
    titulo: "Tempo de resposta esgotado",
    explicacao:
      "O WhatsApp demorou demais para confirmar o envio e a tentativa foi encerrada. Em boa parte dos casos a mensagem chega mesmo assim.",
    acao: "Confira no WhatsApp antes de reenviar, para não mandar duplicado.",
  },
  {
    re: /the number does not exist|not.*registered on whatsapp|invalid.*recipient|undeliverable/i,
    titulo: "O número não existe no WhatsApp",
    explicacao:
      "O destinatário informado não tem uma conta de WhatsApp ativa, ou o número está incorreto.",
    acao: "Confira o número no cadastro do cliente e corrija antes de tentar de novo.",
  },
  {
    re: /número de telefone mal formatado/i,
    titulo: "Número de telefone mal formatado",
    explicacao:
      "O número chegou num formato que o WhatsApp não reconhece — normalmente falta o DDI, sobra caractere ou o DDD está incompleto.",
    acao: "Ajuste o número no cadastro para o formato com DDI e DDD (ex.: 5511999999999).",
  },
  {
    re: /lista de supressão|opt-?out/i,
    titulo: "Destinatário pediu para não receber",
    explicacao:
      "Este número respondeu pedindo para parar de receber mensagens e entrou na lista de supressão. O bloqueio protege a saúde do seu número.",
    acao: "Só volte a enviar se o cliente pedir para ser reativado.",
  },
  {
    re: /permissão para mandar mensagens em grupos/i,
    titulo: "Seu plano não permite envio para grupos",
    explicacao:
      "A mensagem foi endereçada a um grupo, e o plano contratado não inclui esse recurso.",
    acao: "Fale com o suporte para liberar o envio em grupos.",
  },
  {
    re: /only absolute urls are supported|cannot navigate to invalid url|invalid url/i,
    titulo: "URL do arquivo inválida",
    explicacao:
      "O endereço do anexo não é uma URL completa e pública, então não foi possível baixar o arquivo para enviar.",
    acao: "Use o link completo do arquivo, começando com https://",
  },
  {
    re: /status code 404/i,
    titulo: "Arquivo não encontrado",
    explicacao: "O anexo não existe mais no endereço informado, ou o link expirou.",
    acao: "Gere o arquivo novamente e confira se o link abre no navegador.",
  },
  {
    re: /status code 40[13]/i,
    titulo: "Sem permissão para acessar o arquivo",
    explicacao:
      "O anexo existe, mas está protegido: o servidor recusou o download. Arquivos com login ou restrição de acesso não podem ser enviados.",
    acao: "Publique o arquivo num endereço de acesso livre e tente de novo.",
  },
  {
    re: /status code 429|rate limit|too many|throughput/i,
    titulo: "Limite de solicitações excedido",
    explicacao:
      "Foram feitas requisições demais em pouco tempo e o servidor recusou temporariamente.",
    acao: "Aguarde alguns minutos e tente novamente, reduzindo o ritmo dos disparos.",
  },
  {
    re: /cannot read propert(y|ies) of undefined/i,
    titulo: "A conexão do número não estava pronta",
    explicacao:
      "A mensagem foi processada enquanto a conexão ainda subia, e o envio não pôde ser concluído.",
    acao: "Aguarde dois minutos e reenvie. Se repetir, leia o QR Code novamente.",
  },
  { re: /24\s*hours|re-?engagement|last replied/i, codigoMeta: 131047 },
  { re: /template name .*does not exist|does not exist in|template.*not.*found/i, codigoMeta: 132001 },
  { re: /number of (localizable_)?params|parameter count|expected \d+ param/i, codigoMeta: 132000 },
  { re: /restricted|account.*locked|disabled/i, codigoMeta: 131031 },
  { re: /invalid parameter/i, codigoMeta: 100 },
];

function normalizarCodigo(codigo) {
  if (codigo === null || codigo === undefined) return null;

  const texto = String(codigo).trim();
  return texto || null;
}

/**
 * Traduz o erro de uma mensagem para linguagem de cliente final.
 *
 * Devolve `null` quando não há erro nenhum. Quando há erro mas o motivo não é
 * reconhecido, devolve um descritor genérico com `generica: true` e o texto cru
 * preservado — melhor um título honesto em português do que o inglês do Baileys
 * jogado na tela do cliente.
 */
export function traduzirErro(erro) {
  if (!erro) return null;

  const texto = erro.text || erro.details || erro.title || "";
  const codigo = normalizarCodigo(erro.code);

  if (!texto && !codigo) return null;

  let entrada = codigo ? META_POR_CODIGO[codigo] : null;

  if (!entrada && texto) {
    const acerto = POR_TEXTO.find((padrao) => padrao.re.test(texto));

    if (acerto) {
      entrada = acerto.codigoMeta ? META_POR_CODIGO[acerto.codigoMeta] : acerto;
    }
  }

  if (entrada) {
    return {
      titulo: entrada.titulo,
      explicacao: entrada.explicacao,
      acao: entrada.acao,
      tecnico: texto || null,
      code: codigo,
    };
  }

  return {
    titulo: "Não foi possível enviar a mensagem",
    explicacao:
      "O envio falhou por um motivo que o painel ainda não sabe explicar. O detalhe técnico abaixo ajuda o suporte a identificar a causa.",
    acao: "Se o erro se repetir, encaminhe o detalhe técnico ao suporte.",
    tecnico: texto || null,
    code: codigo,
    generica: true,
  };
}
